<script setup>
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import api from 'dashboard/api/aceleres/integrations';
const emit = defineEmits(['refreshKeys']);
const state = ref({ inboxes: [], counts: { all: 0, active: 0, archived: 0 } });
const filter = ref('all');
const busy = ref(false);
const error = ref('');
const feedback = ref('');
const selected = ref(null);
const preview = ref(null);
const keepContacts = ref(true);
const keepCards = ref(true);
const confirmation = ref('');
const rows = computed(() =>
  state.value.inboxes.filter(
    i =>
      filter.value === 'all' ||
      (filter.value === 'archived' ? i.archived : !i.archived)
  )
);
const load = async () => {
  error.value = '';
  try {
    state.value = await api.getManagedInboxes();
  } catch (e) {
    error.value = e.message;
  }
};
let timer;
onMounted(() => {
  load();
  timer = setInterval(() => {
    if (!busy.value && state.value.inboxes.some(i => i.deleting)) load();
  }, 5000);
});
onBeforeUnmount(() => clearInterval(timer));
const act = async (inbox, action) => {
  busy.value = true;
  error.value = '';
  try {
    state.value =
      action === 'archive'
        ? await api.archiveManagedInbox(inbox.id)
        : await api.restoreManagedInbox(inbox.id);
    feedback.value =
      action === 'archive'
        ? 'Inbox arquivada. A chave voltou a ficar disponível.'
        : 'Inbox restaurada. Confira o estado da conexão na aba correspondente; pode ser necessário reconectar.';
    emit('refreshKeys');
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
const openDelete = async inbox => {
  selected.value = inbox;
  preview.value = null;
  confirmation.value = '';
  keepContacts.value = true;
  keepCards.value = true;
  error.value = '';
  try {
    preview.value = await api.getManagedInboxPreview(inbox.id);
  } catch (e) {
    error.value = e.message;
  }
};
const deletedItems = computed(() => {
  const items = [
    'Conversas e mensagens',
    'Cards de atendimento',
    'Conexão deste canal',
  ];
  if (!keepContacts.value) items.push('Contatos sem outros vínculos');
  if (!keepCards.value) items.push('Cards do funil exclusivos desta inbox');
  return items;
});
const remove = async () => {
  if (confirmation.value !== 'APAGAR' || !preview.value || busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    state.value = await api.deleteManagedInbox(selected.value.id, {
      confirmation: confirmation.value,
      keep_contacts: keepContacts.value,
      keep_funnel_cards: keepCards.value,
    });
    selected.value = null;
    emit('refreshKeys');
    feedback.value =
      'Exclusão solicitada. A limpeza do histórico está em andamento.';
  } catch (e) {
    error.value = e.message;
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="integracoes-page__panel integracoes-inboxes rounded-2xl border border-n-weak bg-n-solid-1 p-[18px] text-[13px] md:min-h-[calc(100vh-180px)]"
  >
    <header class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="!mb-0 !text-[10px] !leading-4 font-semibold tracking-widest text-n-slate-11">
          CAIXAS DE ENTRADA
        </p>
        <h2 class="mt-1 text-lg font-semibold">Inboxes</h2>
        <p class="mt-1 !mb-0 !text-[12px] !leading-5 text-n-slate-11">
          Arquive, restaure ou apague as caixas de entrada da empresa. Apagar
          ou arquivar libera a chave de instância.
        </p>
      </div>
      <div class="flex rounded-xl bg-n-alpha-2 p-1">
        <button
          v-for="option in [
            { key: 'all', name: 'Todas' },
            { key: 'active', name: 'Ativas' },
            { key: 'archived', name: 'Arquivadas' },
          ]"
          :key="option.key"
          class="rounded-lg px-3 py-2 text-xs"
          :class="
            filter === option.key ? 'bg-n-solid-1 shadow-sm' : 'text-n-slate-11'
          "
          @click="filter = option.key"
        >
          {{ option.name }} · {{ state.counts[option.key] }}
        </button>
      </div>
    </header>
    <p v-if="feedback" class="mt-4 text-sm text-[#15803D]" role="status">
      {{ feedback }}
    </p>
    <p
      v-if="error && !selected"
      class="mt-4 text-sm text-n-ruby-11"
      role="alert"
    >
      {{ error }}
      <button class="underline" @click="load">Tentar novamente</button>
    </p>
    <div class="mt-5 overflow-x-auto rounded-xl border border-n-weak">
      <table class="integracoes-inboxes__table w-full text-left !text-[13px]">
        <thead class="bg-[#F8F9FA] text-[10px] uppercase text-n-slate-11">
          <tr>
            <th class="p-4">Inbox</th>
            <th class="p-4">Canal</th>
            <th class="p-4">Responsável</th>
            <th class="p-4">Conversas</th>
            <th class="p-4">Status</th>
            <th class="p-4" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="inbox in rows"
            :key="inbox.id"
            class="border-t border-n-weak"
            :class="inbox.archived ? 'text-n-slate-10' : ''"
          >
            <td class="p-4">
              <strong>{{ inbox.name }}</strong
              ><small class="mt-1 block text-n-slate-11">{{
                inbox.identity
              }}</small>
            </td>
            <td class="p-4">{{ inbox.channel }}</td>
            <td class="p-4">{{ inbox.responsible }}</td>
            <td class="p-4">{{ inbox.conversations }} conversas</td>
            <td class="p-4">
              <span
                class="rounded-full px-3 py-1 text-xs font-semibold"
                :class="
                  inbox.archived
                    ? 'bg-n-alpha-2'
                    : 'bg-[#E8F5EC] text-[#15803D]'
                "
                >{{
                  inbox.deleting
                    ? 'Em exclusão'
                    : inbox.archived
                      ? 'Arquivada'
                      : 'Ativa'
                }}</span
              >
            </td>
            <td class="p-4 text-right">
              <button
                v-if="!inbox.archived"
                :disabled="busy"
                class="integracoes-inboxes__archive rounded-lg !border !border-solid !border-[#E4E6EA] !px-3 !py-2 !text-[12px]"
                @click="act(inbox, 'archive')"
              >
                Arquivar</button
              ><template v-else
                ><button
                  v-if="!inbox.deleting"
                  :disabled="busy"
                  class="integracoes-inboxes__restore mr-2 rounded-lg !border !border-solid !border-[#E4E6EA] !px-3 !py-2 !text-[12px] !text-[#202428] hover:bg-n-slate-3 disabled:opacity-50"
                  @click="act(inbox, 'restore')"
                >
                  Restaurar</button
                ><button
                  :disabled="busy"
                  class="integracoes-inboxes__delete rounded-lg !border !border-solid !border-[#F3D9D6] !px-3 !py-2 !text-[12px] text-[#B42318]"
                  @click="openDelete(inbox)"
                >
                  {{ inbox.deleting ? 'Repetir exclusão' : 'Apagar' }}
                </button></template
              >
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="6" class="p-4 text-n-slate-11">
              Nenhuma inbox neste filtro.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <Teleport to="body"
      ><div
        v-if="selected"
        class="fixed inset-0 z-[120] flex items-center justify-center bg-modal-backdrop-dark p-4"
        @click.self="!busy && (selected = null)"
      >
        <section
          class="integracoes-inboxes__delete-dialog max-h-[90vh] w-full max-w-[608px] overflow-y-auto rounded-[18px] bg-n-solid-1 p-6 shadow-xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-inbox-title"
        >
          <h2 id="delete-inbox-title" class="text-lg font-semibold">
            Apagar a inbox "{{ selected.name }}"?
          </h2>
          <p class="mt-2 !mb-0 !text-[13px] text-n-slate-11">
            Escolha o que manter. O resto será apagado de vez e não pode ser
            recuperado.
          </p>
          <p class="mt-5 text-xs font-semibold tracking-wide text-n-slate-11">
            O QUE VOCÊ QUER MANTER?
          </p>
          <label class="integracoes-inboxes__retention mt-3 !flex !items-start gap-3 rounded-xl border border-n-weak p-4 !leading-5"
            ><input
              v-model="keepContacts"
              class="integracoes-inboxes__keep-contacts mt-0.5 h-[18px] w-[18px] shrink-0 accent-[#15803D]"
              type="checkbox"
              :disabled="busy"
            /><span
              ><strong class="text-sm">Manter os contatos</strong
              ><small class="mt-1 block text-n-slate-11"
                >Continuam no Zoraxy e no Acelerity ({{
                  preview?.contacts ?? '…'
                }}
                contatos).</small
              ></span
            ></label
          ><label class="integracoes-inboxes__retention mt-3 !flex !items-start gap-3 rounded-xl border border-n-weak p-4 !leading-5"
            ><input
              v-model="keepCards"
              class="integracoes-inboxes__keep-cards mt-0.5 h-[18px] w-[18px] shrink-0 accent-[#15803D]"
              type="checkbox"
              :disabled="busy"
            /><span
              ><strong class="text-sm">Manter os cards no funil</strong
              ><small class="mt-1 block text-n-slate-11"
                >Os cards continuam nas etapas do funil, sem as
                conversas.</small
              ></span
            ></label
          >
          <div
            class="mt-4 rounded-xl bg-[#FEF3F2] p-4 text-[13px] text-[#B42318]"
          >
            <strong>Será apagado</strong>
            <ul class="mt-2 list-disc pl-4">
              <li v-for="item in deletedItems" :key="item">{{ item }}</li>
            </ul>
            <p class="mt-2">
              A vaga de WhatsApp volta a ficar disponível. Canais sociais que
              compartilham a chave são preservados.
            </p>
            <p v-if="!keepContacts" class="mt-2">
              Contatos com outros atendimentos, tarefas ou cards permanecem
              protegidos.
            </p>
          </div>
          <label class="mt-5 block text-sm"
            >Para confirmar, digite <strong>APAGAR</strong
            ><input
              v-model="confirmation"
              autocomplete="off"
              class="mt-2 w-full rounded-lg border border-n-weak bg-n-solid-1 p-3"
              :disabled="busy"
          /></label>
          <p v-if="error" role="alert" class="mt-3 text-sm text-n-ruby-11">
            {{ error }}
          </p>
          <footer class="mt-6 flex justify-end gap-2">
            <button
              :disabled="busy"
              class="integracoes-inboxes__cancel !h-10 rounded-lg !border !border-solid !border-[#E4E6EA] !px-4 !py-0 !text-[13px]"
              @click="selected = null"
            >
              Cancelar</button
            ><button
              :disabled="busy || confirmation !== 'APAGAR' || !preview"
              class="h-10 rounded-lg bg-[#B42318] px-4 text-sm font-semibold text-white disabled:opacity-40"
              @click="remove"
            >
              Apagar inbox
            </button>
          </footer>
        </section>
      </div></Teleport
    >
  </section>
</template>
